// React

// Material UI
import { Badge } from '@mui/material';
import { DateCalendar, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { PickersDay as PickersDayDefault } from '@mui/x-date-pickers/PickersDay';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { grey } from '@mui/material/colors';

// Redux
import { useSelector, useDispatch } from 'react-redux';
import { setSelectedDate } from '../../../../../store/calendarSlice';

// Utils
import { compareAsc } from 'date-fns';

const WEEKDAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const AgendaPicker = () => {
  const calendarTheme = createTheme({
    typography: {
      fontFamily: ['Quicksand', 'sans-serif'],
    },
    components: {
      MuiTypography: {
        styleOverrides: {
          h4: { fontSize: '2rem' },
          body1: { fontSize: '20px' },
        },
      },
      MuiDateCalendar: {
        styleOverrides: {
          root: {
            width: '100%',
            maxWidth: '350px',
            maxHeight: 'none',
            height: 'auto',
          },
        },
      },
      MuiPickersCalendarHeader: {
        styleOverrides: {
          root: {
            marginTop: 0,
            marginBottom: '24px',
            justifyContent: 'center',
            columnGap: '10px',
          },
          label: {
            fontWeight: 'bold',
          },
          switchViewButton: {
            display: 'none',
          },
        },
      },
      MuiPickersArrowSwitcher: {
        styleOverrides: {
          button: {
            padding: 0,
            color: '#F17023',
          },
        },
      },
      MuiDayCalendar: {
        styleOverrides: {
          header: {
            '& > .MuiDayCalendar-weekDayLabel:first-of-type': {
              color: '#FAB97A',
            },
          },
          weekDayLabel: {
            fontWeight: 700,
            color: '#C6CED2',
            fontSize: '16px',
          },
          weekContainer: {
            '& > :first-of-type .MuiPickersDay-root': {
              fontWeight: 500,
              fontSize: '16px',
            },
            '& .MuiPickersDay-root': {
              fontWeight: 500,
              fontSize: '16px',
            },
          },
        },
      },
      MuiPickersDay: {
        styleOverrides: {
          root: {
            color: grey['900'],
            '&.Mui-selected, &.Mui-selected:focus, &.Mui-selected:hover, &.Mui-selected.Mui-focusVisible': {
              backgroundColor: '#ef6c00',
              color: '#fff',
              fontWeight: 700,
            },
            '&.Mui-selected:hover': {
              backgroundColor: '#ff9d4d',
            },
          },
          today: {
            borderColor: '#ef6c00',
          },
        },
      },
      MuiBadge: {
        styleOverrides: {
          colorSecondary: {
            backgroundColor: 'rgba(90, 105, 114, 0.5)',
          },
        },
      },
    },
  });

  const dispatch = useDispatch();

  const selectedDate = useSelector((state) => state.calendar.selectedDate);
  const agendaEvents = useSelector((state) => state.calendar.agendaEvents);

  const changeDate = (value) => {
    const today = new Date(new Date().setHours(0, 0, 0, 0));
    if (compareAsc(today, value) !== 1) {
      dispatch(setSelectedDate(value));
    } else {
      dispatch(setSelectedDate(today));
    }
  };

  // Custom day component that renders a Badge dot on days with events.
  const DayWithBadge = (props) => {
    const day = props.day;
    const isSunday = day.getDay() === 0;
    const isSelected = !props.outsideCurrentMonth && agendaEvents.some(
      (event) =>
        event.startFormatted.getDate() === day.getDate() &&
        event.startFormatted.getMonth() === day.getMonth() &&
        event.startFormatted.getFullYear() === day.getFullYear()
    );
    return (
      <Badge
        color="secondary"
        variant="dot"
        overlap="circular"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        invisible={!isSelected}
      >
        <PickersDayDefault
          {...props}
          sx={isSunday && !props.selected ? { color: '#FAB97A !important' } : undefined}
        />
      </Badge>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <ThemeProvider theme={calendarTheme}>
        <DateCalendar
          views={['day']}
          value={selectedDate}
          minDate={new Date()}
          onChange={(value) => changeDate(value)}
          onMonthChange={(value) => changeDate(value)}
          slots={{ day: DayWithBadge }}
          dayOfWeekFormatter={(date) => WEEKDAY_LABELS[date.getDay()]}
        />
      </ThemeProvider>
    </LocalizationProvider>
  );
};

export default AgendaPicker;
